import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router';

import { type INTEGRATION_PROVIDER_ID, useIntegrationProviderDetails } from '@/api/integration';
import Error404 from '@/components/errors/Error404';
import PageError from '@/components/errors/PageError';
import { PageSpinner } from '@/components/loaders';
import { Page } from '@/components/page';
import { ReturnToLink } from '@/components/ReturnToLink';
import { Link, Text, Title } from '@/components/typography';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import { getProviderHelpLinkById, getProviderLongDescriptionById } from '../providerUtil';
import IntegrationDetailRail from './IntegrationDetailRail';

type ConnectionResultsType = {
  connectionSavedSuccess?: boolean;
  connectionSavedError?: boolean;
};

const IntegrationDetail = () => {
  const { pushNotification } = useNotification();
  const { provider: providerId } = useParams();

  const { state } = useLocation();
  const { connectionSavedSuccess, connectionSavedError } = (state as ConnectionResultsType) || {};
  const { provider, isLoading, is404, error } = useIntegrationProviderDetails(
    providerId as INTEGRATION_PROVIDER_ID,
  );

  useEffect(() => {
    if (provider) {
      if (connectionSavedSuccess === false) {
        pushNotification({
          type: 'error',
          message:
            connectionSavedError ??
            t`Oh no! Something went wrong. Please, try syncing again or contact your administrator`,
        });
      }
      if (connectionSavedSuccess === true) {
        pushNotification({
          type: 'success',
          message: (
            <span>
              <Text variant="caption1" weight="semi-bold">
                <Trans>
                  Success! You have connected your {provider.name} to Channel99! Learn more about
                  the {provider.name} connection{' '}
                  <Link
                    color="black"
                    variant="caption1"
                    weight="semi-bold"
                    to={getProviderHelpLinkById(provider.id)}
                  >
                    HERE
                  </Link>
                </Trans>
              </Text>
            </span>
          ),
        });
      }
    }
  }, [connectionSavedSuccess, provider, connectionSavedError, pushNotification]);

  return (
    <Page title={t`Integrations`} pageName={provider?.name} exampleAIQuestions={[]}>
      <div className="flex flex-col gap-6">
        <ReturnToLink route={ROUTES.integrationByCategory()} routeName={t`Integrations`} />
        {is404 && <Error404 title={<Trans>The integration provider cannot be found</Trans>} />}
        {error && (
          <PageError
            message={<Trans>An error occurred while loading this integration provider</Trans>}
            detail={<Trans>Please refresh the page and try again</Trans>}
            headingLevel={2}
          />
        )}
        {isLoading && <PageSpinner />}
        {!is404 && !error && !isLoading && provider && (
          <div className="flex gap-6.5">
            <IntegrationDetailRail provider={provider} />
            <div className="flex flex-1 flex-col gap-5">
              <Title level={2} weight="light">
                <Trans>Overview</Trans>
              </Title>
              {getProviderLongDescriptionById(provider?.id)?.map((text, index) => (
                <Text key={index} variant="headline" weight="light">
                  {text}
                </Text>
              ))}
              <Text variant="headline" weight="light">
                <Trans>
                  For more details please review our{' '}
                  <Link
                    to={getProviderHelpLinkById(provider.id)}
                    variant="headline"
                    target="_blank"
                    showExternalIcon
                  >
                    Support Center
                  </Link>
                </Trans>
              </Text>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
};

export default IntegrationDetail;
